import { BaseFilter } from './BaseFilter.js'

/**
 * Base class for booking-specific filters
 */
export class BookingFilter extends BaseFilter {
	constructor(id, label, active = false) {
		super(id, label, active)
		this.type = 'booking'
	}

	/**
	 * Apply filter to a booking/event object
	 * @param {Object} booking - Booking/event object
	 * @param {Object} context - Additional context
	 * @returns {boolean}
	 */
	apply(booking, context = {}) {
		throw new Error('apply() method must be implemented by booking filter subclasses')
	}
}

/**
 * Filter bookings by usage type (Nutzungsart)
 */
export class UsageTypeFilter extends BookingFilter {
	constructor(active = false, selectedUsageTypeIds = []) {
		super('usage-type', 'Nutzungsart', active)
		this.selectedUsageTypeIds = new Set(selectedUsageTypeIds.map(id => parseInt(id)))
		this.usageTypeOptions = new Map() // id -> name mapping
	}

	/**
	 * Set available usage type options
	 * @param {Array} usageTypeList - Array of {id, title} objects
	 */
	setUsageTypeOptions(usageTypeList) {
		this.usageTypeOptions.clear()
		usageTypeList.forEach(type => {
			this.usageTypeOptions.set(parseInt(type.id), type.title)
		})
	}

	/**
	 * Add usage type to selection
	 * @param {number} usageTypeId 
	 */
	addUsageType(usageTypeId) {
		this.selectedUsageTypeIds.add(parseInt(usageTypeId))
		this._updateActiveState()
	}

	/**
	 * Remove usage type from selection
	 * @param {number} usageTypeId 
	 */
	removeUsageType(usageTypeId) {
		this.selectedUsageTypeIds.delete(parseInt(usageTypeId))
		this._updateActiveState()
	}

	/**
	 * Set single usage type (replace existing selection)
	 * @param {number} usageTypeId 
	 */
	setUsageType(usageTypeId) {
		this.selectedUsageTypeIds.clear()
		if (usageTypeId) {
			this.selectedUsageTypeIds.add(parseInt(usageTypeId))
		}
		this._updateActiveState()
	}

	/**
	 * Clear all usage type selections
	 */
	clearSelection() {
		this.selectedUsageTypeIds.clear()
		this.setActive(false)
	}

	/**
	 * Get selected usage type ID (for single select)
	 */
	getSelectedUsageTypeId() {
		const ids = Array.from(this.selectedUsageTypeIds)
		return ids.length > 0 ? ids[0] : null
	}

	/**
	 * Get selected usage type name
	 */
	getSelectedUsageTypeName() {
		const id = this.getSelectedUsageTypeId()
		return id ? this.usageTypeOptions.get(id) : null
	}

	/**
	 * Update active state based on selection
	 * @private
	 */
	_updateActiveState() {
		this.active = this.selectedUsageTypeIds.size > 0
	}

	/**
	 * Get display label for the filter
	 */
	getDisplayLabel() {
		const name = this.getSelectedUsageTypeName()
		return name ? name : this.label
	}

	apply(booking, context = {}) {
		if (!this.active || this.selectedUsageTypeIds.size === 0) return true;
		
		// Check booking type ID
		const bookingTypeId = booking.extendedProps?.bookingTypeId || booking.bookingTypeId
		if (!bookingTypeId) return false;
		
		return this.selectedUsageTypeIds.has(parseInt(bookingTypeId));
	}

	getConfig() {
		return {
			...super.getConfig(),
			selectedUsageTypeId: this.getSelectedUsageTypeId(),
			displayLabel: this.getDisplayLabel()
		}
	}

	serialize() {
		return {
			...super.serialize(),
			selectedUsageTypeId: this.getSelectedUsageTypeId()
		}
	}

	deserialize(state) {
		super.deserialize(state)
		if (state.selectedUsageTypeId) {
			this.setUsageType(state.selectedUsageTypeId)
		} else {
			this.clearSelection()
		}
	}
}

/**
 * Filter bookings by person (who booked)
 */
export class PersonFilter extends BookingFilter {
	constructor(active = false, selectedPersonId = null, selectedPersonName = null) {
		super('person-filter', 'Person suchen', active)
		this.selectedPersonId = selectedPersonId
		this.selectedPersonName = selectedPersonName || ''
		this.searchQuery = ''
	}

	/**
	 * Set selected person (replaces any existing selection)
	 * @param {number} personId 
	 * @param {string} personName 
	 */
	setSelectedPerson(personId, personName) {
		// Always clear previous selection first
		this._clearInternalState()
		
		if (personId && personName) {
			this.selectedPersonId = parseInt(personId)
			this.selectedPersonName = personName
			// Don't set searchQuery to personName - search field should be empty after selection
		}
		
		this._updateActiveState()
	}

	/**
	 * Clear person selection completely
	 */
	clearSelection() {
		this._clearInternalState()
		this.setActive(false)
	}

	/**
	 * Clear internal state without affecting active status
	 * @private
	 */
	_clearInternalState() {
		this.selectedPersonId = null
		this.selectedPersonName = ''
		this.searchQuery = ''
	}

	/**
	 * Set search query only (doesn't affect selection)
	 * @param {string} query 
	 */
	setSearchQuery(query) {
		this.searchQuery = query || ''
	}

	/**
	 * Check if a person is currently selected
	 * @returns {boolean}
	 */
	hasSelection() {
		return !!this.selectedPersonId
	}

	/**
	 * Get selected person info
	 */
	getSelectedPerson() {
		return {
			id: this.selectedPersonId,
			name: this.selectedPersonName
		}
	}

	/**
	 * Update active state based on selection
	 * @private
	 */
	_updateActiveState() {
		this.active = !!this.selectedPersonId
	}

	/**
	 * Get display label for the filter
	 */
	getDisplayLabel() {
		return this.selectedPersonName || this.label
	}

	apply(booking, context = {}) {
		if (!this.active || !this.selectedPersonId) return true;
		
		// Check various person ID fields in booking
		const extendedProps = booking.extendedProps || {}
		const studentId = extendedProps.studentId || booking.studentId
		const employeeId = extendedProps.employeeId || booking.employeeId
		const bookingUserId = extendedProps.bookingUserId || booking.bookingUserId
		
		return studentId === this.selectedPersonId || 
			   employeeId === this.selectedPersonId ||
			   bookingUserId === this.selectedPersonId;
	}

	getConfig() {
		return {
			...super.getConfig(),
			selectedPersonId: this.selectedPersonId,
			selectedPersonName: this.selectedPersonName,
			searchQuery: this.searchQuery,
			displayLabel: this.getDisplayLabel(),
			hasSelection: this.hasSelection()
		}
	}

	serialize() {
		return {
			...super.serialize(),
			selectedPersonId: this.selectedPersonId,
			selectedPersonName: this.selectedPersonName
		}
	}

	deserialize(state) {
		super.deserialize(state)
		this.selectedPersonId = state.selectedPersonId || null
		this.selectedPersonName = state.selectedPersonName || ''
		// Keep searchQuery empty - don't populate with person name
		this._updateActiveState()
	}
}